import React from 'react';
import { Typography, Stack, Alert, Button } from '@mui/material';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import { PORTAL_LINK_UNAVAILABLE, portalTokenErrorMessage } from './portalTokenMessages';

/**
 * Default /p/t/:token landing — validated token home.
 */
const PortalTokenHome = () => {
  const { token, validation, validationError } = useOutletContext() || {};

  if (validationError || (validation && validation.valid === false)) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">
          Link unavailable
        </Typography>
        <Alert severity="warning">
          {validationError || portalTokenErrorMessage(validation) || PORTAL_LINK_UNAVAILABLE}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Ask your landlord or property manager for a new secure link.
        </Typography>
      </Stack>
    );
  }

  const actionType = validation?.actionType;
  const canMaintenance = actionType === 'submit_maintenance';
  const canAccess = actionType === 'confirm_access';
  const canDocuments = actionType === 'view_documents';

  let cta = null;
  if (canMaintenance) {
    cta = (
      <Button
        component={RouterLink}
        to={`/p/t/${encodeURIComponent(token || '')}/maintenance/new`}
        variant="contained"
        size="large"
        fullWidth
        sx={{ py: 1.5 }}
      >
        Submit maintenance request
      </Button>
    );
  } else if (canAccess) {
    const eventId = validation?.relatedEntityId;
    const accessPath = eventId
      ? `/p/t/${encodeURIComponent(token || '')}/access/${encodeURIComponent(eventId)}`
      : `/p/t/${encodeURIComponent(token || '')}/access`;
    cta = (
      <Button
        component={RouterLink}
        to={accessPath}
        variant="contained"
        size="large"
        fullWidth
        sx={{ py: 1.5 }}
      >
        Respond to access request
      </Button>
    );
  } else if (canDocuments) {
    cta = (
      <Button
        component={RouterLink}
        to={`/p/t/${encodeURIComponent(token || '')}/documents`}
        variant="contained"
        size="large"
        fullWidth
        sx={{ py: 1.5 }}
      >
        View your documents
      </Button>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h1">
        Your landlord shared a secure link
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Use this page to complete a specific action. Your details stay private and are not shown in
        the link.
      </Typography>
      {cta || (
        <Alert severity="info">
          This link is for a different action. Open the full URL your landlord sent, or ask them for
          a new link.
        </Alert>
      )}
    </Stack>
  );
};

export default PortalTokenHome;
